import { z } from 'zod';
import type { PageLayout } from './customization';
import type { FieldDef, ObjectDef, ObjectName } from './objects';
import type { ObjectPermissions } from './permissions';

// ---------------------------------------------------------------------------
// Custom objects: org-defined record types with their own list views, record pages, permissions,
// sharing model, custom fields (including formulas and lookups to standard records) and API.
// Records of every custom object share one table, `custom_records`, scoped by object.
// ---------------------------------------------------------------------------

export type CustomObjectName = `co_${string}`;
export const CUSTOM_OBJECT_NAME_RE = /^co_[a-z0-9_]{1,40}$/;
export const CUSTOM_OBJECTS_PER_ORG = 50;
export const isCustomObjectName = (s: string): s is CustomObjectName => CUSTOM_OBJECT_NAME_RE.test(s);

/** API name from a label: `co_` + lowercase letters, digits and underscores. */
export function customObjectApiName(label: string): CustomObjectName {
  const slug = label.normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40);
  return `co_${slug || 'object'}`;
}

export const CUSTOM_SHARING_MODELS = ['private', 'public_read', 'public_read_write'] as const;

export const customObjectSchema = z
  .object({
    label: z.string().trim().min(1).max(80),
    pluralLabel: z.string().trim().min(1).max(80),
    /** Label of the record name field, e.g. "Project name". */
    nameLabel: z.string().trim().min(1).max(80).default('Name'),
    description: z.string().trim().max(1000).nullable().optional(),
    sharingModel: z.enum(CUSTOM_SHARING_MODELS).default('private'),
  })
  .strict();

export interface CustomObjectDef {
  id: string;
  apiName: CustomObjectName;
  label: string;
  pluralLabel: string;
  nameLabel: string;
  description: string | null;
  sharingModel: (typeof CUSTOM_SHARING_MODELS)[number];
}

const flag = z.boolean().default(false);
export const customObjectPermissionsSchema = z
  .object({
    permissions: z.array(z.object({ permissionSetId: z.string().uuid(), read: flag, create: flag, edit: flag, delete: flag, viewAll: flag, modifyAll: flag }).strict()).max(200),
  })
  .strict();

/** Applies the same implications as standard objects (Modify All ⇒ everything, Edit ⇒ Read, …). */
export function normalizeObjectPermissions(p: Partial<ObjectPermissions>): ObjectPermissions {
  const o: ObjectPermissions = { read: !!p.read, create: !!p.create, edit: !!p.edit, delete: !!p.delete, viewAll: !!p.viewAll, modifyAll: !!p.modifyAll };
  if (o.modifyAll) Object.assign(o, { read: true, edit: true, delete: true, viewAll: true });
  if (o.viewAll || o.edit || o.create || o.delete) o.read = true;
  return o;
}

/** Defaults for a new object, by permission set key: admins everything, managers see all, users their own. */
export function defaultCustomObjectPermissions(key: string | null): ObjectPermissions {
  if (key === 'system_admin') return normalizeObjectPermissions({ modifyAll: true, create: true });
  if (key === 'sales_manager' || key === 'presales_manager') return normalizeObjectPermissions({ create: true, edit: true, delete: true, viewAll: true });
  if (key === 'presales_engineer') return normalizeObjectPermissions({ create: true, edit: true, delete: true, viewAll: true });
  if (key === 'sales_user') return normalizeObjectPermissions({ create: true, edit: true, delete: true });
  // Projects only: no custom records either.
  if (key === 'project_manager' || key === 'project_engineer') return normalizeObjectPermissions({});
  return normalizeObjectPermissions({ read: true });
}

const STANDARD_FIELDS = (nameLabel: string): FieldDef[] => [
  { name: 'name', column: 'name', label: nameLabel, type: 'text', required: true, maxLength: 255, listDefault: true, searchable: true, tracked: true },
  { name: 'ownerId', column: 'owner_id', label: 'Owner', type: 'user', required: true, listDefault: true, tracked: true },
  { name: 'createdAt', column: 'created_at', label: 'Created', type: 'datetime', readOnly: true },
  { name: 'updatedAt', column: 'updated_at', label: 'Last modified', type: 'datetime', readOnly: true, listDefault: true },
];

/**
 * The object definition before custom fields are added. `name` carries the API name; it is typed as
 * an ObjectName only so the generic field helpers (queries, forms, formulas) can be reused.
 */
export function customObjectBase(def: Pick<CustomObjectDef, 'apiName' | 'label' | 'pluralLabel' | 'nameLabel'>): ObjectDef {
  return {
    name: def.apiName as unknown as ObjectName,
    table: 'custom_records',
    label: def.label,
    pluralLabel: def.pluralLabel,
    nameField: 'name',
    defaultSort: { field: 'name', dir: 'asc' },
    fields: STANDARD_FIELDS(def.nameLabel),
  };
}

/** What the session tells the web app about each custom object the person can read. */
export interface CustomObjectInfo {
  def: CustomObjectDef;
  schema: ObjectDef;
  permissions: ObjectPermissions;
  layout: PageLayout | null;
  /** Reference fields of this object, by the standard object they point at (for related lists). */
  parents: { field: string; label: string; object: ObjectName }[];
}
